<script setup>
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useTherapistStore } from "@/stores/therapist";
import CalendarView from "@/pages/admin/components/CalendarView.vue";
import { useLocationStore } from "@/stores/location";

const props = defineProps({
    therapistId: {
        type: String,
        required: true
    },
    locationId: {
        type: String,
        required: true
    },
    autoclose: {
        type: Boolean,
        default: false
    }
});

const router = useRouter();
const therapistStore = useTherapistStore();
const locationStore = useLocationStore();
const therapist = computed(() => therapistStore.therapists.find((t) => t.ROWID === props.therapistId));
const location = computed(() => locationStore.locations.find((l) => l.ROWID === props.locationId));

function handleActionComplete() {
    if (!props.autoclose) return;
    router.push({
        name: "therapist-details",
        params: { therapistId: props.therapistId }
    });
}

function goBackToTherapist() {
    router.push({
        name: "therapist-details",
        params: { therapistId: props.therapistId }
    });
}

const eventDuration = {
    absent: null,
    hart_hours: null,
    booking: therapist.value?.session_time,
    hold: therapist.value?.session_time
};

onMounted(async () => {
    if (!therapistStore.therapists.length) await therapistStore.fetchTherapists();
    if (!locationStore.locations.length) await locationStore.fetchTherapistLocations(props.therapistId);
    if (!therapist.value) router.push("/admin/therapists");
    if (!location.value) router.push("therapist-details", { therapistId: props.therapistId });
});
</script>

<template>
    <div v-if="therapist" class="mb-4 flex items-center justify-between">
        <div class="flex items-center space-x-4">
            <button
                @click="goBackToTherapist"
                class="px-3 py-1 text-gray-600 hover:text-gray-800 flex items-center space-x-1"
            >
                <span>←</span>
                <span>Back to Therapist</span>
            </button>
            <h2 class="text-xl font-semibold">
                {{ therapist.first_name }} {{ therapist.last_name }}'s Calendar
            </h2>
        </div>
        <span v-if="location" class="text-xl font-semibold">
            {{ location.name }}
        </span>
    </div>
    <div v-if="therapist && location">
        <CalendarView
            :therapist="therapist"
            :location="location"
            :mode="$route.query.mode"
            :modeEventsOnly="$route.query.modeEventsOnly"
            :slot-duration="eventDuration[$route.query.mode]"
            @done="handleActionComplete"
        />
    </div>
    <div v-else>
        Loading required data from the server...
    </div>
</template>
